/* eslint-disable @typescript-eslint/no-explicit-any */
import { ChangeEvent } from 'react';
import { types, flow, Instance } from 'mobx-state-tree';
import { AxiosResponse } from 'axios';
import axios from 'server';
import { ClientModel, IClientModel } from './models/client.model';
import { ServiceStatusesModel, IServiceStatusesModel } from './models/serviceStatuses.model';
import { /*ClientRequest,*/ LoginLinkRequest, ClientTableView } from './types';
import { capitalizeFirstLetter, toFormatDate } from 'utils';
import { ClientData } from './SubForms/ClientGenericForm';
// import { ClientData } from 'components/Forms/ClientForm/SubForms/ClientGenericForm';

const { model, array, string, number, reference, maybe } = types;

export const ClientsTableStore = model('ClientsTableStore', {
  clients: array(ClientModel),
  selectedIds: array(number),
  clickedId: number,
  selectedClient: maybe(reference(ClientModel)),
  loginLink: string,
  serviceStatuses: array(ServiceStatusesModel),
  filter: string,
  groupFilter: array(number),
})
  .views((self) => ({
    get clientsView(): ClientTableView[] {
      const normalizedClients = self.clients.map(
        (item): ClientTableView => ({
          ...item,
          dateRegistration: toFormatDate(item.dateRegistration),
          renewalDate: toFormatDate(item.renewalDate),
          client: `${item.name} ${item.lastName} ${item.email}`,
          license: `${item.periodName} (${item.clientNumberOfUserAccountName})`,
        })
      );

      const groupedClients = normalizedClients.filter(
        (client) => self.groupFilter.findIndex((statusId) => client.serviceStatus === statusId) > -1
      );

      if (!self.filter) {
        return groupedClients;
      }

      const keywordRegExp = new RegExp(self.filter, 'i');
      return groupedClients.filter(
        ({
          name,
          email,
          phone,
          organizationName,
          organizationDomain,
          serviceStatus,
          dateRegistration,
          serviceStatusName,
          registerStatusName,
          renewalDate,
          clientNumberOfUserAccountName,
        }) =>
          keywordRegExp.test(
            `${name}${email}${phone}${toFormatDate(
              dateRegistration
            )}${organizationName}${organizationDomain}${serviceStatus}${dateRegistration}${serviceStatusName}${renewalDate}${clientNumberOfUserAccountName}${registerStatusName}`
          )
      );
    },
    getActionDescription(id: string): string {
      const statusName = self.serviceStatuses.find((status) => status.id === +id);
      return statusName ? capitalizeFirstLetter(statusName.value) : '';
    },
    getRenderValueForGroupSelect(): string {
      const statusValues: string[] = [];
      for (let i = 0; i < self.serviceStatuses.length; i++) {
        if (self.groupFilter.indexOf(self.serviceStatuses[i].id) > -1) {
          statusValues.push(capitalizeFirstLetter(self.serviceStatuses[i].value));
        }
      }
      if (self.groupFilter.indexOf(0) > -1) {
        statusValues.push('New Clients');
      }
      return statusValues.join(', ');
    },
    hasAuthTokenUrl(id: number): boolean {
      const result = self.clients.find((client) => client.id === id);
      return !!result?.AuthTokenUrl as boolean;
    },
  }))
  .actions((self) => ({
    clearSelected(): void {
      self.selectedIds.clear();
      self.clickedId = 0;
    },
  }))
  .actions((self) => ({
    normalizeClients({ data }: AxiosResponse): IClientModel[] {
      self.clearSelected();
      self.clients.replace(data.data);
      return self.clients;
    },
    normalizeServiceStatuses({ data }: AxiosResponse): IServiceStatusesModel[] {
      self.serviceStatuses.replace(data.data);
      self.groupFilter.replace(self.serviceStatuses.map(({ id }) => id));
      self.groupFilter.push(0);
      return self.serviceStatuses;
    },
    normalizeLoginLink({ data }: AxiosResponse): string {
      self.clearSelected();
      self.loginLink = data.data.url;
      window.open(self.loginLink, '_blank');
      return self.loginLink;
    },
    setSelectedIds(data: number[]): void {
      self.selectedIds.replace(data);
    },
    setClickedId(clickedId: number): void {
      self.clickedId = clickedId;
      self.selectedClient = self.clients.find(({ id }) => id === self.clickedId);
    },
    setFilter(filter: string): void {
      self.filter = filter;
    },
    setGroupFilter(event: ChangeEvent<{ value: unknown }>): void {
      self.groupFilter.replace(event.target.value as number[]);
    },
  }))
  .actions((self) => ({
    getClients: flow(function* () {
      const result = yield axios
        .get<IClientModel[]>('admin/client')
        .then(self.normalizeClients)
        .catch((error) => {
          throw new Error(error);
        });
      return result;
    }),
    getServiceStatuses: flow(function* () {
      const result = yield axios
        .get<IServiceStatusesModel[]>('admin/client/clients-status')
        .then(self.normalizeServiceStatuses)
        .catch((error) => {
          throw new Error(error);
        });
      return result;
    }),
    updateBulkServiceStatus: flow(function* (serviceStatus: string) {
      const result = yield axios
        .post<IClientModel[]>('admin/client/update-clients-status', {
          clientIds: self.selectedIds,
          serviceStatus,
        })
        .then(self.normalizeClients)
        .catch((error) => {
          throw new Error(error);
        });
      return result;
    }),
    // updateServiceStatus: flow(function* (serviceStatus: number) {
    //   const result = yield axios
    //     .put<IClientModel[]>(`admin/client/${self.clickedId}`, {
    //       serviceStatus,
    //     })
    //     .then(self.clearSelected)
    //     .catch((error) => {
    //       throw new Error(error);
    //     });
    //   return result;
    // }),
    updateServiceStatus: flow(function* (serviceStatus: number) {
      const result = yield axios
        .post<IClientModel[]>(`admin/client/update-clients-status`, {
          clientIds: [self.clickedId],
          serviceStatus,
        })
        .then(self.clearSelected)
        .catch((error) => {
          throw new Error(error);
        });
      return result;
    }),
    updateAppVersion: flow(function* (serviceStatus: number) {
      const result = yield axios
        .post<IClientModel[]>(`admin/client/update-client-app-version`, {
          clientIds: [self.clickedId],
          serviceStatus,
        })
        .then(self.clearSelected)
        .catch((error) => {
          throw new Error(error);
        });
      return result;
    }),
    updateBulkAppVersion: flow(function* (serviceStatus: string) {
      const result = yield axios
        .post<IClientModel[]>('admin/client/update-client-app-version', {
          clientIds: self.selectedIds,
          serviceStatus,
        })
        .then(self.normalizeClients)
        .catch((error) => {
          throw new Error(error);
        });
      return result;
    }),
    createClientStepOne: flow(function* (data: ClientData) {
      const result = yield axios.post<ClientData>('client/free-trial/step-one', data).catch((error) => {
        throw new Error(error.response.data.data.message);
      });
      return result;
    }),
    createClientStepTwo: flow(function* (data: ClientData) {
      const result = yield axios.post<ClientData>('client/free-trial/step-two', data).catch((error) => {
        throw new Error(error.response.data.data.message);
      });
      return result;
    }),
    createClient: flow(function* (data: ClientData) {
      const result = yield axios.post<ClientData>('client', data).catch((error) => {
        throw new Error(error.response.data.data.message);
      });
      return result;
    }),
    getLoginLink: flow(function* () {
      const result = yield axios
        .post<LoginLinkRequest>(
          '',
          { email: self.selectedClient?.email, loginToken: self.selectedClient?.loginToken },
          { baseURL: self.selectedClient?.AuthTokenUrl }
        )
        .then(self.normalizeLoginLink)
        .catch((error) => {
          throw new Error(error);
        });
      return result;
    }),
  }));

export type IClientsTableStore = Instance<typeof ClientsTableStore>;
